/**
 * Whether a device's app build can receive driver messages.
 *
 * WHY THIS EXISTS AS A SHARED HELPER. The same comparison was duplicated byte-for-byte in
 * `DriverMessage.tsx` and `SendDriverMessageTab.tsx`. Two copies of a rule that silently
 * removes recipients agree on the day they are written and drift the first time one is
 * fixed — and this one needed fixing.
 *
 * THE DEFECT IT REPLACES. The old regex was `/C-(\d+\.\d+\.?\d*)/`, requiring a literal
 * `C-` prefix, and any string that did not match fell straight to "App too old":
 *
 *     C-1.392                      eligible
 *     L-1.403.20-local-90bf681b    "APP TOO OLD"   <- 1.403 > 1.387 by a wide margin
 *
 * That build is not old. It is differently prefixed — a local build rather than a released
 * one — and the portal excluded it from the recipient list while telling the operator its
 * app was out of date. Found 2026-08-25 on device 2, which had been silently unreachable
 * for driver messages since it moved to a local build.
 *
 * SO THE PARSE IS PREFIX-AGNOSTIC. Any leading label is accepted, or none at all. What is
 * required is a dotted numeric version somewhere in the string.
 */

/** The build from which the app understands driver messages. Unchanged from the original. */
export const DRIVER_MESSAGE_MIN_APP_VERSION = '1.387.0'

export type appVersionVerdict =
  /** Parsed, and at or above the minimum. */
  | 'supported'
  /** Parsed, and genuinely below the minimum — the app really is too old. */
  | 'tooOld'
  /**
   * No version to compare. Either absent, or in a shape this does not recognise.
   *
   * **Deliberately NOT folded into `tooOld`.** They call for different words in front of an
   * operator: "update the app" is actionable and, for an unrecognised string, wrong. Merging
   * them is what produced the original defect — the code failed closed, which was defensible,
   * and then reported the reason as a fact, which was not.
   */
  | 'unknown'

/**
 * Pulls a dotted numeric version out of a build string, prefix or no prefix.
 *
 * Returns null when there is nothing version-shaped to compare. Note it takes the FIRST
 * dotted-numeric run: `L-1.403.20-local-90bf681b` yields `1.403.20`, and the trailing commit
 * hash is ignored rather than confusing the parse.
 */
export const parseAppVersion = (appVersion: unknown): number[] | null => {
  if (typeof appVersion !== 'string') {
    return null
  }
  const match = appVersion.match(/(\d+)\.(\d+)(?:\.(\d+))?/)
  if (!match) {
    return null
  }
  return [Number(match[1]), Number(match[2]), Number(match[3] ?? 0)]
}

const compare = (left: number[], right: number[]): number => {
  for (let index = 0; index < 3; index += 1) {
    if ((left[index] ?? 0) !== (right[index] ?? 0)) {
      return (left[index] ?? 0) - (right[index] ?? 0)
    }
  }
  return 0
}

/**
 * THREE outcomes, not two. The caller decides what to say about each; this decides only
 * what is true.
 */
export const appVersionVerdictFor = (
  appVersion: unknown,
  minimum: string = DRIVER_MESSAGE_MIN_APP_VERSION
): appVersionVerdict => {
  const parsed = parseAppVersion(appVersion)
  if (!parsed) {
    return 'unknown'
  }
  const floor = parseAppVersion(minimum)
  if (!floor) {
    return 'unknown'
  }
  return compare(parsed, floor) >= 0 ? 'supported' : 'tooOld'
}

/**
 * The gate itself. Unrecognised still fails CLOSED — the portal cannot know that an
 * unparseable build understands driver messages, and offering a recipient that silently
 * drops them is worse than excluding one that would have worked.
 *
 * What changes is that the caller can now say WHICH it is.
 */
export const supportsDriverMessages = (appVersion: unknown): boolean =>
  appVersionVerdictFor(appVersion) === 'supported'
